/**
 * Amplify auth adapter for `native` (username/password) and `federated` (Cognito-brokered SSO).
 */

import { Amplify } from "aws-amplify"
import {
  type AuthUser,
  fetchAuthSession,
  getCurrentUser,
  signIn,
  signInWithRedirect,
  signOut,
} from "aws-amplify/auth"
import { Hub } from "aws-amplify/utils"
import type { AuthEventHandlers, AuthStrategy, UserInfo } from "./auth"
import { getConfig, getFederatedConfig } from "../config"
import { buildAmplifyConfig } from "./amplifyConfig"
import { logoutBounceUrl } from "./federatedLogout"

export class AmplifyAuthStrategy implements AuthStrategy {
  private configured = false

  /**
   * Configure Amplify lazily (runtime config is loaded before the app renders).
   */
  private ensureConfigured(): void {
    if (this.configured) return
    Amplify.configure(buildAmplifyConfig())
    this.configured = true
  }

  async login(username: string, password: string): Promise<UserInfo> {
    this.ensureConfigured()
    // Clear any stale session (no-op if already signed out).
    await signOut().catch(() => {})
    await signIn({ username, password })
    const userInfo = await this.getCurrentUserInfo()
    if (!userInfo) {
      throw new Error("Failed to get user information after login")
    }
    return userInfo
  }

  async loginWithRedirect(): Promise<void> {
    this.ensureConfigured()
    await signInWithRedirect({ provider: { custom: getFederatedConfig().idpProviderName } })
  }

  /**
   * Return null: Amplify handles the callback and emits a Hub event that `subscribe` reports.
   */
  async completeRedirectCallback(): Promise<UserInfo | null> {
    return null
  }

  async getCurrentUserInfo(): Promise<UserInfo | null> {
    this.ensureConfigured()
    // getCurrentUser throws when nobody is signed in, which is normal, so stay silent. Log only
    // failures after a user is found; separate awaits avoid matching Amplify's minified error names.
    let user: AuthUser
    try {
      user = await getCurrentUser()
    } catch {
      return null
    }

    try {
      const session = await fetchAuthSession()

      const idToken = session.tokens?.idToken

      // cognito:username is the login name, not the sub.
      const cognitoUsername = idToken
        ? (idToken.payload["cognito:username"] as string) || user.username
        : user.signInDetails?.loginId || user.username

      const email = idToken?.payload.email as string | undefined

      return { principalId: cognitoUsername, email, isAuthenticated: true }
    } catch (error) {
      console.error("Signed-in user found but their session could not be resolved:", error)
      return null
    }
  }

  async getApiToken(): Promise<string> {
    const session = await fetchAuthSession()
    return session.tokens?.idToken?.toString() ?? ""
  }

  async logout(): Promise<void> {
    // Federated sign-out redirects to the bounce page, which ends the upstream IDP session.
    if (getConfig().authMode === "federated") {
      await signOut({ global: false, oauth: { redirectUrl: logoutBounceUrl() } })
    } else {
      await signOut()
    }
  }

  subscribe(handlers: AuthEventHandlers): () => void {
    return Hub.listen("auth", ({ payload }) => {
      if (payload.event === "signInWithRedirect") {
        void this.getCurrentUserInfo().then((userInfo) => {
          if (userInfo) {
            handlers.onSignedIn(userInfo)
          } else {
            handlers.onSignInError("Failed to get user information after sign-in.")
          }
        })
      } else if (payload.event === "signInWithRedirect_failure") {
        handlers.onSignInError("Sign-in did not complete. Please try again.")
      }
    })
  }
}
